import { Alert, Button, CircularProgress, DialogContent, Stack, Typography } from '@mui/material';
import { ResponsiveDialog } from '@components/Dialogs/ResponsiveDialog';
import { useGetTokenPurposeContextQuery, useRenewAccessTokenMutation, useGetDocumentQuery } from 'gql/index';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AccessTokenPurpose, TokenStatus } from 'gql/index';
import { DocumentSignatureContent } from './TokenPurposeContent/DocumentSignatureContent';
import { FormFillContent } from './TokenPurposeContent/FormFillContent';
import { ViewDocumentContent } from './TokenPurposeContent/ViewDocumentContent';
import { WorkflowSubmitContent } from './TokenPurposeContent/WorkflowSubmitContent';
import { SignatureDialog } from '@modules/documents/components/DocumentSignatureDialog';

interface TokenPurposeDialogProps {
  token: string;
  open: boolean;
  onClose: () => void;
  disableClose?: boolean;
}

export const TokenPurposeDialog: React.FC<TokenPurposeDialogProps> = ({ token, open, onClose, disableClose = false }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [renewalRequested, setRenewalRequested] = useState(false);

  const { data, isLoading, error } = useGetTokenPurposeContextQuery({ token }, {
    enabled: open && !!token,
    select: (data) => data.tokenPurposeContext,
  });

  const context = data;

  const { data: documentData } = useGetDocumentQuery(
    { id: context?.documentId ?? 0 },
    {
      enabled: isSignatureDialogOpen && !!context?.documentId && context?.purpose === AccessTokenPurpose.DocumentSignature,
    }
  );

  const { mutate: renewToken, isPending: isRenewing } = useRenewAccessTokenMutation({
    onSuccess: () => {
      if (disableClose) {
        setRenewalRequested(true);
      } else {
        onClose();
      }
    },
  });

  if (isLoading) {
    return (
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        title={formatMessage({ id: 'Loading...' })}
        disableClose={disableClose}
        hideCloseButton={disableClose}
      >
        <DialogContent>
          <Stack alignItems="center" justifyContent="center" p={3}>
            <CircularProgress />
          </Stack>
        </DialogContent>
      </ResponsiveDialog>
    );
  }

  if (error || !context) {
    return (
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        title={formatMessage({ id: 'Error' })}
        disableClose={disableClose}
        hideCloseButton={disableClose}
      >
        <DialogContent>
          <Stack spacing={2} p={3}>
            <Alert severity="error">
              {formatMessage({ id: 'Unable to load token information. Please try again.' })}
            </Alert>
            {!disableClose && (
              <Button variant="contained" onClick={onClose}>
                {formatMessage({ id: 'Close' })}
              </Button>
            )}
          </Stack>
        </DialogContent>
      </ResponsiveDialog>
    );
  }

  const handleAction = () => {
    if (context.purpose === AccessTokenPurpose.DocumentSignature && context.documentId) {
      setIsSignatureDialogOpen(true);
    } else if (context.actionUrl) {
      navigate(context.actionUrl);
      onClose();
    }
  };

  const handleSignatureDialogClose = () => {
    setIsSignatureDialogOpen(false);
  };

  const handleSignatureDialogSuccess = () => {
    setIsSignatureDialogOpen(false);
    onClose();
  };

  const handleRenew = () => {
    if (token) {
      renewToken({ token });
    }
  };

  const renderContent = () => {
    switch (context.purpose) {
      case AccessTokenPurpose.DocumentSignature:
        return <DocumentSignatureContent context={context} onAction={handleAction} onRenew={handleRenew} isRenewing={isRenewing} />;
      case AccessTokenPurpose.ViewDocument:
        return <ViewDocumentContent context={context} onAction={handleAction} onRenew={handleRenew} isRenewing={isRenewing} />;
      case AccessTokenPurpose.FormFill:
        return <FormFillContent context={context} onAction={handleAction} onRenew={handleRenew} isRenewing={isRenewing} />;
      case AccessTokenPurpose.WorkflowSubmit:
        return <WorkflowSubmitContent context={context} onAction={handleAction} onRenew={handleRenew} isRenewing={isRenewing} />;
      default:
        return (
          <Alert severity="error">
            {formatMessage({ id: 'Unknown token purpose.' })}
          </Alert>
        );
    }
  };

  const getTitle = () => {
    if (context.status === TokenStatus.Completed) {
      return formatMessage({ id: 'Already Completed' });
    }
    if (context.status === TokenStatus.Expired) {
      return formatMessage({ id: 'Link Expired' });
    }
    if (context.status === TokenStatus.Invalid) {
      return formatMessage({ id: 'Invalid Link' });
    }
    return formatMessage({ id: 'Action Required' });
  };

  const onCloseClicked = (event: React.MouseEvent<HTMLButtonElement>, reason: string) => {
    const canClose = !disableClose || renewalRequested;
    if (!canClose) {
      return;
    }
    if (reason === 'backdropClick') {
      event.preventDefault();
    } else {
      onClose();
    }
  };

  const renderBody = () => {
    if (renewalRequested) {
      return (
        <Stack spacing={3} p={3}>
          <Alert severity="success">
            {formatMessage({ id: 'Check your emails for the new link. You can now close this window.' })}
          </Alert>
          {context.initiatorName && (
            <Typography variant="body1" color="text.secondary">
              {formatMessage({ id: 'Requested by {name}' }, { name: context.initiatorName })}
            </Typography>
          )}
          {context.resourceName && (
            <Typography variant="body2" color="text.secondary">
              {context.resourceName}
            </Typography>
          )}
        </Stack>
      );
    }
    return renderContent();
  };

  return (
    <>
      <ResponsiveDialog
        open={open}
        onClose={onCloseClicked}
        title={getTitle()}
        disableClose={disableClose}
        hideCloseButton={disableClose}
      >
        <DialogContent>
          {renderBody()}
        </DialogContent>
      </ResponsiveDialog>
      {context?.documentId && context?.projectId && (
        <SignatureDialog
          documentId={context.documentId}
          projectId={context.projectId}
          actionId={documentData?.document?.zohoActionId ?? ''}
          requestId={documentData?.document?.zohoRequestId ?? ''}
          open={isSignatureDialogOpen}
          onClose={handleSignatureDialogClose}
          onSuccess={handleSignatureDialogSuccess}
        />
      )}
    </>
  );
};
